// All date/time operations in this app are anchored to East African Time
// (Africa/Nairobi). This keeps behaviour consistent regardless of where the
// frontend is served from (Vercel edge regions) or the user's browser locale.
export const APP_TZ = "Africa/Nairobi";

function nairobiParts(d = new Date()) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    hour: Number(get("hour")),
    minute: Number(get("minute")),
  };
}

export function formatKES(n) {
  const num = Number(n) || 0;
  return new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 0,
  }).format(num);
}

// YYYY-MM-DD in Nairobi time, regardless of where the code runs.
export function toLocalDate(d = new Date()) {
  const p = nairobiParts(d);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

export function today() {
  return toLocalDate(new Date());
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// "3rd-Mar-2026" — bare YYYY-MM-DD strings render as-is; Date instances
// and other values are formatted in Nairobi time.
export function formatDate(value) {
  if (!value) return "—";
  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const [, y, mo, d] = m;
      return `${ordinal(Number(d))}-${MONTHS[Number(mo) - 1]}-${y}`;
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const p = nairobiParts(d);
  return `${ordinal(p.day)}-${MONTHS[p.month - 1]}-${p.year}`;
}

export function formatDateTime(value) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const p = nairobiParts(d);
  const hh = String(p.hour).padStart(2, "0");
  const mm = String(p.minute).padStart(2, "0");
  return `${ordinal(p.day)}-${MONTHS[p.month - 1]}-${p.year} · ${hh}:${mm}`;
}

// "April 2026"
export function formatMonth(yyyymm) {
  if (!yyyymm) return "—";
  const [y, m] = yyyymm.split("-").map(Number);
  if (!y || !m) return yyyymm;
  return `${MONTHS_LONG[m - 1]} ${y}`;
}

export function currentMonth(ref = new Date()) {
  const p = nairobiParts(ref);
  return `${p.year}-${String(p.month).padStart(2, "0")}`;
}

export function addMonths(yyyymm, delta) {
  const [y, m] = yyyymm.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

// Returns a list of months around `ref`, from -12 to +3.
export function monthOptions(ref = new Date()) {
  const cur = currentMonth(ref);
  const out = [];
  for (let i = -12; i <= 3; i++) {
    const m = addMonths(cur, i);
    out.push({ value: m, label: formatMonth(m) });
  }
  return out.reverse(); // newest first
}

// Extract the recurring day-of-month from a tenant.
export function tenantDueDay(tenant) {
  if (!tenant) return 1;
  if (typeof tenant.dueDay === "number" && tenant.dueDay >= 1 && tenant.dueDay <= 31) {
    return tenant.dueDay;
  }
  // Backward-compat: derive from legacy dueDate (YYYY-MM-DD).
  if (tenant.dueDate) {
    const d = Number(String(tenant.dueDate).slice(8, 10));
    if (d >= 1 && d <= 31) return d;
  }
  return 1;
}

// The concrete due date for a tenant in a given YYYY-MM month (clamped to month length).
// Returns a local-date string (YYYY-MM-DD) without going through UTC conversion.
export function tenantDueDateFor(tenant, yyyymm) {
  const [y, m] = yyyymm.split("-").map(Number);
  const day = tenantDueDay(tenant);
  const lastOfMonth = new Date(y, m, 0).getDate();
  const clamped = Math.min(day, lastOfMonth);
  return `${y}-${String(m).padStart(2, "0")}-${String(clamped).padStart(2, "0")}`;
}

// Sum RENT payments for a tenant in a specific month.
// Only counts payments where chargeType is "rent" or undefined (backward compat).
export function paidForMonth(payments, tenantId, yyyymm) {
  return (payments || [])
    .filter((p) => {
      if (p.tenantId !== tenantId || p.month !== yyyymm) return false;
      const ct = p.chargeType || "rent";
      return ct === "rent";
    })
    .reduce((a, p) => a + (Number(p.amount) || 0), 0);
}

// Returns "PAID" | "PARTIAL" | "PENDING" | "OVERDUE" | "VACATED"
// Checks the requested month AND scans backward for any prior unpaid months
// (arrears) — if a previous month's rent was never fully paid, the tenant
// is OVERDUE regardless of the current month's status.
export function computeTenantStatus(tenant, payments, yyyymm, now = new Date()) {
  if (!tenant) return "PENDING";
  if (tenant.status === "VACATED") return "VACATED";
  const month = yyyymm || currentMonth(now);
  const rent = Number(tenant.rent) || 0;
  const currentM = currentMonth(now);

  // Check actual payment coverage first so a fully- or partly-paid
  // month shows its real state regardless of past arrears.
  const paid = paidForMonth(payments || [], tenant.id, month);
  if (rent > 0 && paid >= rent) return "PAID";
  if (paid > 0) return "PARTIAL";

  // Future months are never OVERDUE — the bill isn't due yet, even
  // if the tenant has unpaid past months. Past-month arrears still
  // surface when the user filters the current or an earlier month.
  if (month > currentM) return "PENDING";

  // Arrears: scan from tenant's start month to the month before `month`.
  if (rent > 0 && hasArrears(tenant, payments, month)) return "OVERDUE";

  if (month < currentM) return "OVERDUE";
  // Same month: compare today to the due day.
  const dueIso = tenantDueDateFor(tenant, month);
  const todayIso = toLocalDate(now);
  return todayIso > dueIso ? "OVERDUE" : "PENDING";
}

// Returns true if any month from tenant creation to the month BEFORE `upToMonth`
// has outstanding (unpaid) rent.
function hasArrears(tenant, payments, upToMonth) {
  const rent = Number(tenant.rent) || 0;
  if (rent <= 0) return false;
  // Determine start month from tenant creation date.
  const created = tenant.createdAt || "2025-01";
  const startMonth = created.slice(0, 7); // YYYY-MM
  let m = startMonth;
  while (m < upToMonth) {
    const paid = paidForMonth(payments || [], tenant.id, m);
    if (paid < rent) return true;
    m = addMonths(m, 1);
  }
  return false;
}

export function tenantBalance(tenant, payments, yyyymm) {
  const rent = Number(tenant.rent) || 0;
  const paid = paidForMonth(payments, tenant.id, yyyymm);
  return Math.max(0, rent - paid);
}

// ── Utility billing helpers ─────────────────────────────────────────────

// Sum payments for a tenant+month filtered by chargeType.
// undefined/missing chargeType on a payment is treated as "rent".
export function paidForMonthByType(payments, tenantId, yyyymm, chargeType) {
  return (payments || [])
    .filter((p) => {
      if (p.tenantId !== tenantId || p.month !== yyyymm) return false;
      const pType = p.chargeType || "rent";
      return pType === chargeType;
    })
    .reduce((a, p) => a + (Number(p.amount) || 0), 0);
}

// Returns the utility charge amount for a tenant in a given month, or 0.
export function utilityChargeForMonth(utilityCharges, tenantId, yyyymm, type) {
  const entry = (utilityCharges || []).find(
    (uc) => uc.tenantId === tenantId && uc.month === yyyymm && uc.type === type
  );
  return entry ? Number(entry.amount) || 0 : 0;
}

// Outstanding balance for a specific utility type in a given month.
export function utilityBalanceForMonth(utilityCharges, payments, tenantId, yyyymm, type) {
  const charged = utilityChargeForMonth(utilityCharges, tenantId, yyyymm, type);
  const paid = paidForMonthByType(payments, tenantId, yyyymm, type);
  return Math.max(0, charged - paid);
}

// Scans backward for unpaid utility bills (arrears) from tenant creation to before `upToMonth`.
export function hasUtilityArrears(tenant, utilityCharges, payments, upToMonth, type) {
  if (!tenant) return false;
  const startMonth = (tenant.createdAt || "2025-01").slice(0, 7);
  let m = startMonth;
  while (m < upToMonth) {
    const charged = utilityChargeForMonth(utilityCharges, tenant.id, m, type);
    if (charged > 0) {
      const paid = paidForMonthByType(payments, tenant.id, m, type);
      if (paid < charged) return true;
    }
    m = addMonths(m, 1);
  }
  return false;
}

// Total monthly charges: rent + water + electricity.
export function totalMonthlyDue(tenant, utilityCharges, yyyymm) {
  const rent = Number(tenant?.rent) || 0;
  const water = utilityChargeForMonth(utilityCharges, tenant?.id, yyyymm, "water");
  const electricity = utilityChargeForMonth(utilityCharges, tenant?.id, yyyymm, "electricity");
  return rent + water + electricity;
}

// Total outstanding balance across all charge types.
export function totalBalanceForMonth(tenant, utilityCharges, payments, yyyymm) {
  const rentBal = tenantBalance(tenant, payments, yyyymm);
  const waterBal = utilityBalanceForMonth(utilityCharges, payments, tenant?.id, yyyymm, "water");
  const elecBal = utilityBalanceForMonth(utilityCharges, payments, tenant?.id, yyyymm, "electricity");
  return rentBal + waterBal + elecBal;
}

// Returns the next date the tenant owes rent, based on what they've paid.
// If the current month is already settled, rolls forward to the following month.
// Use this for forward-looking messages like pending reminders.
export function tenantNextDueDate(tenant, payments, now = new Date()) {
  if (!tenant) return null;
  const rent = Number(tenant.rent) || 0;
  const currentM = currentMonth(now);
  const paid = paidForMonth(payments || [], tenant.id, currentM);
  if (rent > 0 && paid >= rent) {
    return tenantDueDateFor(tenant, addMonths(currentM, 1));
  }
  return tenantDueDateFor(tenant, currentM);
}
